import { createContext } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useLocalStorage, useAsync } from 'react-use';
import { useUser } from '@izettle/react';
import { captureMessage } from '@sentry/react';
import { useAuthClient } from '../hooks/use-auth.js';
import { getServiceUrl } from '../utils/rest.js';
import { jsx } from 'react/jsx-runtime';

let UrlType = /*#__PURE__*/function (UrlType) {
  UrlType["Base"] = "baseurls";
  UrlType["Service"] = "serviceurls";
  return UrlType;
}({});
let KnownService = /*#__PURE__*/function (KnownService) {
  KnownService["Advance"] = "ADVANCE_SERVICE";
  KnownService["Attestation"] = "SAMSUNG_ATTESTATION";
  KnownService["Auth"] = "AUTHORIZATION_SERVICE";
  KnownService["CardPayment"] = "CARD_PAYMENT";
  KnownService["CashRegister"] = "CASH_REGISTER_SERVICE";
  KnownService["Checkout"] = "CHECKOUT_SERVICE";
  KnownService["DocUpload"] = "DOCUMENT_UPLOAD_SERVICE";
  KnownService["GiftCard"] = "GIFTCARD_SERVICE";
  KnownService["GraphQL"] = "GRAPHQL_SERVICE";
  KnownService["Image"] = "IMAGE_SERVICE";
  KnownService["Inventory"] = "INVENTORY_SERVICE";
  KnownService["Invoice"] = "INVOICE_SERVICE";
  KnownService["Keyval"] = "KEYVAL_SERVICE";
  KnownService["KeyIn"] = "KEYIN_SERVICE";
  KnownService["Klarna"] = "KLARNA_SERVICE";
  KnownService["KycGap"] = "KYC_GAP_SERVICE";
  KnownService["Layout"] = "LAYOUT_SERVICE";
  KnownService["Location"] = "LOCATION_SERVICE";
  KnownService["Logging"] = "LOGGING_SERVICE";
  KnownService["Login"] = "LOGIN_WEB";
  KnownService["OAuth2"] = "OAUTH2_SERVICE";
  KnownService["PayByLink"] = "PAYBYLINK_SERVICE";
  KnownService["PayPal"] = "PAYPAL_WEB";
  KnownService["Portal"] = "PORTAL";
  KnownService["ProductLibrary"] = "PRODUCT_LIBRARY_SERVICE";
  KnownService["ProductImportExport"] = "PRODUCT_IMPORT_EXPORT";
  KnownService["ProductSearch"] = "PRODUCT_SEARCH_SERVICE";
  KnownService["Purchase"] = "PURCHASE_SERVICE";
  KnownService["Qrc"] = "QRC_SERVICE";
  KnownService["Refund"] = "CARD_REFUND";
  KnownService["Registration"] = "REGISTRATION_SERVICE";
  KnownService["RegistrationWeb"] = "REGISTRATION_WEB_V2";
  KnownService["Reports"] = "MERCHANT_REPORTS";
  KnownService["Setup"] = "SETUP_SERVICE";
  KnownService["Site"] = "SITE";
  KnownService["Subscription"] = "SUBSCRIPTION_SERVICE";
  KnownService["Tenko"] = "TENKO_SERVICE";
  KnownService["Sun"] = "API";
  KnownService["Tracking"] = "TRACKING";
  KnownService["Webshop"] = "WEBSHOP_SERVICE";
  KnownService["OldRegistrationWeb"] = "REGISTRATION_WEB";
  KnownService["Translations"] = "TRANSLATIONS";
  return KnownService;
}({});
const CACHE_TIMEOUT = 1000 * 60 * 60 * 24;
const getLocalStorageKey = (type, orgUuid) => {
  if (type === UrlType.Base || orgUuid == null) {
    return "iz-base-urls";
  }
  return `iz-urls-${orgUuid.slice(24) || orgUuid.slice(0, 8)}`;
};
const ServiceContext = /*#__PURE__*/createContext({});
const ServiceProvider = _ref => {
  let {
    children,
    type
  } = _ref;
  const handleError = useErrorHandler();

  // User & Auth context might not exist when this is used, so we cast it to `| null`
  const userData = useUser();
  const authClient = useAuthClient();
  const localStorageKey = getLocalStorageKey(type, userData?.organization.fullOrganizationUuid);
  const [urls, setUrls] = useLocalStorage(localStorageKey, null);
  const [fetchedAt, setFetchedAt] = useLocalStorage(`${localStorageKey}-fetched`, 0);
  const shouldRefetch = type === UrlType.Service && urls != null && !Object.keys(urls).includes(KnownService.GraphQL);
  useAsync(async () => {
    if (urls != null && Date.now() - (fetchedAt ?? 0) < CACHE_TIMEOUT && !shouldRefetch) {
      return;
    }
    const response = await fetch(`${getServiceUrl("service-lookup")}/${type}`, {
      headers: type === UrlType.Service ? {
        Authorization: `Bearer ${await authClient?.getToken()}`
      } : undefined
    });
    if (!response.ok && urls == null) {
      return handleError(new Error(`Failed to load '/${type}' from service-lookup without cache to fall back to.`));
    }
    const data = await response.json();
    if (shouldRefetch && !Object.keys(data).includes(KnownService.GraphQL)) {
      captureMessage(`Tried to refetch service URLs due to the GraphQL service missing, but it's still missing. :(`);
    }
    setFetchedAt(Date.now());
    setUrls(data);
  }, []);
  return /*#__PURE__*/jsx(ServiceContext.Provider, {
    value: urls ?? {},
    children: urls && children
  });
};

export { KnownService, ServiceContext, ServiceProvider, UrlType, getLocalStorageKey };
//# sourceMappingURL=services.js.map
